import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, TableCell, TableRow } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import ListTableRowActions from './ListTableRowActions';
import OverflowTooltip from './OverflowTooltip';
import { actionCellSx, getCellContentSx } from './styles';
import { VirtualTableRowType } from './types';
import type { ListTableColumn, ListTableProps, ListTableRow, VirtualTableRow } from './types';

interface ListTableDataRowProps<Row extends ListTableRow> {
  item: Extract<VirtualTableRow<Row>, { type: VirtualTableRowType.DATA | VirtualTableRowType.CHILDREN }>;
  columns: ListTableColumn<Row>[];
  defaultColumnWidth: number;
  rowHeight: number;
  hasActions: boolean;
  rowActions: NonNullable<ListTableProps<Row>['rowActions']>;
  menuActions: NonNullable<ListTableProps<Row>['menuActions']>;
  isRowHighlighted: ListTableProps<Row>['isRowHighlighted'];
  onToggleRow: (key: string, isExpanded: boolean) => void;
}

const ListTableDataRow = <Row extends ListTableRow>({
  item,
  columns,
  defaultColumnWidth,
  rowHeight,
  hasActions,
  rowActions,
  menuActions,
  isRowHighlighted,
  onToggleRow,
}: ListTableDataRowProps<Row>) => {
  const { row, isGrouped } = item;
  const treeRow = item.type === VirtualTableRowType.CHILDREN ? item : undefined;

  return (
    <TableRow
      sx={
        isRowHighlighted?.(row) ? { height: rowHeight, backgroundColor: 'background.success' } : { height: rowHeight }
      }
    >
      {columns.map((column, index) => {
        const width = column.width ?? defaultColumnWidth;
        return (
          <TableCell
            key={column.id}
            align={column.align}
            sx={{ width, p: 0.5, ...(isGrouped && index === 0 && !treeRow ? { pl: 4 } : {}) }}
          >
            {treeRow && index === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width,
                  boxSizing: 'border-box',
                  pl: treeRow.depth * 3 + (isGrouped ? 4 : 0),
                }}
              >
                {treeRow.childCount > 0 && (
                  <Button
                    variant="text"
                    color="neutral"
                    aria-label={`${treeRow.isExpanded ? 'Collapse' : 'Expand'} row ${row.id}`}
                    aria-expanded={treeRow.isExpanded}
                    onClick={() => onToggleRow(treeRow.key, treeRow.isExpanded)}
                    sx={{ mr: 1, padding: '0 !important', border: '0', minWidth: '0' }}
                  >
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: 20,
                        transform: treeRow.isExpanded ? 'none' : 'rotate(-90deg)',
                        transition: 'transform 150ms ease-in-out',
                      }}
                    />
                  </Button>
                )}
                <OverflowTooltip>
                  <Box sx={{ ...getCellContentSx('auto'), flex: 1 }}>
                    {column.render(row)}
                    {treeRow.childCount > 0 && ` · ${treeRow.childCount}`}
                  </Box>
                </OverflowTooltip>
              </Box>
            ) : (
              <OverflowTooltip>
                <Box sx={{ ...getCellContentSx(width), pl: index > 0 ? 1.5 : 0, boxSizing: 'border-box' }}>
                  {column.render(row)}
                </Box>
              </OverflowTooltip>
            )}
          </TableCell>
        );
      })}
      {hasActions && (
        <TableCell align="right" sx={{ ...actionCellSx, width: rowActions.width }}>
          <ListTableRowActions row={row} rowActions={rowActions} menuActions={menuActions} />
        </TableCell>
      )}
    </TableRow>
  );
};

export default ListTableDataRow;
